// A screen's operational status: the one ladder that says whether what a
// customer is looking at right now is worth looking at, and the badge that
// says it.
//
// One ladder rather than two badges, and the rung order is the argument. A
// screen that is switched off is switched off whatever its service is doing,
// and an service that is not reporting makes the estimate's age meaningless —
// so a rung is only reached when none above it applies, and the reader never
// has to work out which of two contradicting badges wins. An "Active"
// badge sitting beside an "Online" one was exactly that contradiction, and
// the reason this lives in one place.
//
// Vocabulary is app-wide and already fixed by `service-health.tsx`: "Offline"
// is what a silent service is called everywhere, and "Stale" belongs to
// estimate freshness and never to services.
//
// It reads three fields and nothing else, so both surfaces that render it —
// the Overview's cards and the workspace-wide screens table — get the same
// answer out of their own row shapes, and a third surface would too.

import { Badge } from "@pej/kore/components";
import { SERVICE_ONLINE_WINDOW_MS } from "@/features/locations/service-health";

/** Mirrors the display page's staleness threshold (spec §6). */
const ESTIMATE_STALE_MS = 5 * 60 * 1000;

/** What the ladder reads. Deliberately fields rather than a row type: the
 *  Overview has a `StatusRow` and the screens table has a `ScreenSearchEntry`,
 *  and neither should have to become the other to ask this question. */
export interface ScreenFreshnessInputs {
    active: boolean;
    /** The *location's* last service contact, not the screen's: one service serves
     *  every screen at a location, so siblings share this answer. Null when no
     *  service has ever connected — a distinct fact, but the same consequence,
     *  so it lands on the same rung. */
    serviceLastSeenAt: string | null;
    /** When the worker last computed this screen's estimate; null if never. */
    latestComputedAt: string | null;
}

export interface Freshness {
    label: string;
    tone: "success" | "warning" | "danger" | "neutral";
}

/** Pure, and `now` is injectable, so every rung is testable without faking
 *  timers — and so a caller rendering several rows can pass one `now` and
 *  keep them consistent with each other. */
export function freshnessOf(input: ScreenFreshnessInputs, now: Date = new Date()): Freshness {
    if (!input.active) return { label: "Inactive", tone: "neutral" };
    const lastSeen = input.serviceLastSeenAt ? Date.parse(input.serviceLastSeenAt) : null;
    if (lastSeen === null || now.getTime() - lastSeen > SERVICE_ONLINE_WINDOW_MS) {
        return { label: "Offline", tone: "danger" };
    }
    if (!input.latestComputedAt) return { label: "No data yet", tone: "warning" };
    if (now.getTime() - Date.parse(input.latestComputedAt) > ESTIMATE_STALE_MS) {
        return { label: "Stale", tone: "warning" };
    }
    return { label: "Live", tone: "success" };
}

/** Takes the inputs rather than a computed `Freshness`, unlike
 *  `ServiceStatusBadge` — that one is handed its status because the locations
 *  table renders the same timestamp again in the next column and the two must
 *  not derive it separately. Nothing beside this badge re-derives anything. */
export interface ScreenFreshnessBadgeProps extends ScreenFreshnessInputs {
    now?: Date;
}

/** No dot, unlike `ServiceStatusBadge`: a dot in this app means the posting service
 *  itself, and this ladder is about the screen. */
export function ScreenFreshnessBadge({ now, ...input }: ScreenFreshnessBadgeProps) {
    const { label, tone } = freshnessOf(input, now);
    return <Badge tone={tone}>{label}</Badge>;
}
